// MACHINERY V2 (Stroje) — ChromeNavV4 right-cluster menus (M2.1).
//
// Reference impl: design_handoff_machinery_v3/machinery-chrome-nav.jsx
// (UserMenu, LangMenu — the `.ms-menu` family).
//
// THESE ARE THE FIRST REAL RADIX PORTALS IN MACHINERY V2, so the portal rule
// from MachineryV2Layout.tsx's header applies here for real, not in theory:
//   • Radix renders *.Content into document.body, OUTSIDE .machinery-v2-root,
//     where --card / --border / --ink / --shadow / --mod-accent do NOT
//     resolve. Every Content below therefore RE-CARRIES
//     `machinery-v2-root p-<palette> mod-stroje` on itself.
//   • Re-carrying the root class also re-applies its opaque `background:
//     var(--bg)` paint, so each Content sets inline background:"transparent"
//     and lets the inner `.ms-menu` div paint the menu surface. Without the
//     transparent override the Content would paint a page-coloured slab.
//   • `palette` is read from machineryV2UiSlice INSIDE each menu component,
//     live on every render — never captured at mount, or a menu opened before
//     a palette flip would keep the old palette while the page changed.
//
// Behaviour is the live app's, unchanged: the logout sequence mirrors
// Header.tsx exactly (accountContext.logout → refreshAccount(null) → sign-in
// with return URL) and the language switch mirrors LanguageSwitcher.tsx
// (i18n.changeLanguage + best-effort customersApi.updateLanguage).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useLocation, useNavigate } from "react-router-dom";

import accountContext from "../../../../account/accountContext";
import { refreshAccount } from "../../../../account/accountSlice";
import { buildAccountSignInWithReturn } from "../../../../account/routes";
import { AccountState } from "../../../../account/models/AccountModels";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation, { SupportedLanguage } from "../../../../shared/hooks/useTranslation";
import { Modules } from "../../../../core/models/PermissionModels";
import customersApi from "../../../shared/api/customersApi";
import { CustomerSettingsState } from "../../../settings/models/models";
import { NgExit, NgGear } from "./ChromeNavIcons";
import { canSeeModule } from "./navModules";

/** Shared portal wrapper — the ONE place the portal rule is implemented. */
const MenuContent: React.FC<{ width: number; children: React.ReactNode }> = ({
  width,
  children,
}) => {
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        className={`machinery-v2-root p-${palette} mod-stroje`}
        align="end"
        sideOffset={8}
        style={{ background: "transparent", zIndex: 1290 }}
      >
        {/* role="none": Radix puts role="menu" on Content and role="menuitem"
            on each Item; this presentational box sits between them, so it must
            not break the ARIA parent-child relationship. */}
        <div className="ms-menu" role="none" style={{ width }}>
          {children}
        </div>
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
};

const LANG_CODES: Record<SupportedLanguage, string> = { sk: "SK", en: "EN", cs: "CZ" };
const LANG_NAMES: Record<SupportedLanguage, string> = {
  sk: "Slovenčina",
  en: "English",
  cs: "Čeština",
};

/** "SK ▾" trigger + language menu. */
export const ChromeLangMenu: React.FC = () => {
  const { translate, changeLanguage, getCurrentLanguage, getSupportedLanguages } =
    useTranslation(["layout", "common"]);
  const current = getCurrentLanguage();

  const select = (lng: SupportedLanguage) => {
    if (lng === current) return;
    changeLanguage(lng);
    customersApi.updateLanguage(lng).catch(() => {
      // silent — localStorage (i18nextLng) is the fallback, same as the
      // legacy LanguageSwitcher.
    });
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={translate("layout:chromeV4.language")}
          style={{
            border: 0,
            background: "transparent",
            padding: "4px 2px",
            cursor: "pointer",
            fontFamily: "var(--font-mono)",
            fontSize: 11.5,
            color: "var(--ink-2)",
            whiteSpace: "nowrap",
          }}
        >
          {LANG_CODES[current] || LANG_CODES.sk} ▾
        </button>
      </DropdownMenu.Trigger>
      <MenuContent width={188}>
        {getSupportedLanguages().map((lng) => (
          <DropdownMenu.Item
            key={lng}
            className="mi"
            onSelect={() => select(lng)}
            style={{
              fontWeight: lng === current ? 600 : 400,
              color: lng === current ? "var(--accent)" : undefined,
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9.5,
                letterSpacing: "0.05em",
                width: 20,
                flexShrink: 0,
                color: "var(--muted)",
              }}
            >
              {LANG_CODES[lng]}
            </span>
            {LANG_NAMES[lng]}
            <span style={{ flex: 1 }} />
            {lng === current && <span style={{ color: "var(--accent)", fontSize: 12 }}>✓</span>}
          </DropdownMenu.Item>
        ))}
      </MenuContent>
    </DropdownMenu.Root>
  );
};

export interface ChromeUserMenuProps {
  /** Active module accent — the avatar adopts it (artboard rule). */
  accent: string;
}

/** Name + avatar trigger, user menu (identity header, Settings, Logout). */
export const ChromeUserMenu: React.FC<ChromeUserMenuProps> = ({ accent }) => {
  const { translate } = useTranslation(["layout", "common"]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector<AccountState>((s: any) => s.account);
  const { customer } = useAppSelector<CustomerSettingsState>((s: any) => s.customerSettings);

  if (!user) return null;

  const initial = user.firstName?.charAt(0)?.toUpperCase() || "?";
  const roleLabel = user.role ? translate("common:roles." + user.role) : "";
  // The company name is loaded by the app shell; while it is absent we show
  // the role alone rather than a placeholder. M2.12 (nav promotion) must make
  // this self-sufficient if the legacy shell stops fetching it.
  const subLine = [roleLabel, customer?.name].filter(Boolean).join(" · ");

  const logout = () => {
    accountContext.logout();
    dispatch(refreshAccount(null));
    navigate(
      buildAccountSignInWithReturn(
        `${location.pathname}${location.search}${location.hash}`
      )
    );
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            border: 0,
            background: "transparent",
            padding: 0,
            cursor: "pointer",
          }}
        >
          <span
            style={{
              fontSize: 12.5,
              color: "var(--ink-2)",
              whiteSpace: "nowrap",
              maxWidth: 110,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user.firstName}
          </span>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              background: accent,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontSize: 11.5,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initial}
          </span>
        </button>
      </DropdownMenu.Trigger>
      <MenuContent width={208}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 12px 9px" }}>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 999,
              background: accent,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initial}
          </span>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ink)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user.firstName}
            </div>
            {subLine && (
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 9.5,
                  color: "var(--faint)",
                  letterSpacing: "0.04em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subLine}
              </div>
            )}
          </div>
        </div>
        <div style={{ height: 1, background: "var(--border-soft)", margin: "0 6px 4px" }} />
        {/* /settings sits behind PermissionRouteGuard(Settings): an ungated
            item would navigate a Settings:None user straight into a denial.
            Mirrors the legacy user menu (Header.tsx:1209). Logout below needs
            no gate, and the identity header is data the user already owns —
            those are the only other items in this menu. */}
        {canSeeModule(Modules.Settings) && (
          <DropdownMenu.Item className="mi" onSelect={() => navigate("/settings")}>
            <span className="mi-ico">
              <NgGear />
            </span>
            {translate("common:navigation.settings")}
          </DropdownMenu.Item>
        )}
        <DropdownMenu.Item className="mi" onSelect={logout}>
          <span className="mi-ico">
            <NgExit />
          </span>
          {translate("common:navigation.logout")}
        </DropdownMenu.Item>
      </MenuContent>
    </DropdownMenu.Root>
  );
};
